import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Loader2, MessageSquare, X } from 'lucide-react'
import { AnimatePresence, MotionConfig, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import { useCallActions } from '@/hooks/useCallActions'
import { useSelectedCallerId } from '@/hooks/useSelectedCallerId'
import { useOuvrirFil, type InboxMatch } from '@/api/inbox'
import type { TimelineEvent } from '@/types/timeline'
import { InboxList } from './InboxList'
import { ContactDetail } from './ContactDetail'
import { FilCollegue } from './FilCollegue'
import { ContactProfile } from './ContactProfile'
import { DetailsAppel } from './DetailsAppel'
import { DetailsAppelContext } from './detailsAppelContext'
import { DialerPane } from './DialerPane'
import { PhoneTab } from './PhoneTab'
import { AccueilEssai } from './AccueilEssai'
import { useMiseEnRouteAFinir } from '@/hooks/useMiseEnRoute'
import { useProfile } from '@/api/profile'

type InboxTabId = 'conversations' | 'appels'

/** Demande de click-to-call captée dans l'URL (?call=…&auto=1). */
interface PendingDial {
  number: string
  auto: boolean
  /** Remonte le clavier à chaque demande (même numéro deux fois de suite). */
  nonce: number
}

/**
 * Boîte de réception : Conversations (une par personne, fil unifié) | Appels
 * (journal d'appels). L'onglet vit dans l'URL : `?tab=appels`, défaut Conversations.
 */
export function InboxTab() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const darkMode = useUIStore((s) => s.darkMode)
  const tab: InboxTabId = searchParams.get('tab') === 'appels' ? 'appels' : 'conversations'

  // Click-to-call : ?call=+14385551234[&auto=1]. On capte le numéro, on retire
  // tout de suite les params (un F5 ne relance pas l'appel) et on ouvre le
  // clavier dans l'onglet Conversations ; DialerPane déclenche l'appel si auto=1.
  const [pendingDial, setPendingDial] = useState<PendingDial | null>(null)
  const [dialerOpen, setDialerOpen] = useState(false)
  const nonceRef = useRef(0)
  useEffect(() => {
    const callParam = searchParams.get('call')
    const autoParam = searchParams.get('auto')
    if (!callParam && !autoParam) return

    if (callParam) {
      nonceRef.current += 1
      setPendingDial({ number: callParam, auto: autoParam === '1', nonce: nonceRef.current })
      setDialerOpen(true)
    }

    const next = new URLSearchParams(searchParams)
    next.delete('call')
    next.delete('auto')
    if (callParam) next.delete('tab')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const setTab = (next: InboxTabId) => {
    const params = new URLSearchParams(searchParams)
    if (next === 'appels') params.set('tab', 'appels')
    else params.delete('tab')
    setSearchParams(params, { replace: true })
  }

  const tabs: { id: InboxTabId; label: string }[] = [
    { id: 'conversations', label: t('inbox.tabs.conversations', 'Conversations') },
    { id: 'appels', label: t('inbox.tabs.calls', 'Appels') },
  ]

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      {/* Onglets Conversations | Appels */}
      <div
        className={cn(
          'flex-shrink-0 flex items-center gap-4 px-4 border-b',
          darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200',
        )}
      >
        {tabs.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              '-mb-px py-2.5 text-[13px] font-medium border-b-2 transition-colors',
              tab === id
                ? darkMode
                  ? 'border-indigo-400 text-white'
                  : 'border-indigo-600 text-slate-900'
                : darkMode
                  ? 'border-transparent text-slate-500 hover:text-slate-300'
                  : 'border-transparent text-slate-400 hover:text-slate-700',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0 flex overflow-hidden">
        {tab === 'appels' ? (
          <PhoneTab />
        ) : (
          <ConversationsPane
            darkMode={darkMode}
            dialerOpen={dialerOpen}
            setDialerOpen={setDialerOpen}
            pendingDial={pendingDial}
          />
        )}
      </div>
    </div>
  )
}

interface PaneProps {
  darkMode: boolean
  dialerOpen: boolean
  setDialerOpen: (open: boolean) => void
  pendingDial: PendingDial | null
}

/** Onglet Conversations : liste | fil unifié (ou clavier) | fiche. */
function ConversationsPane({ darkMode, dialerOpen, setDialerOpen, pendingDial }: PaneProps) {
  const { t } = useTranslation()
  const selectedPhone = useUIStore((s) => s.selectedInboxPhone)
  const selectedLine = useUIStore((s) => s.selectedInboxLine)
  const setSelectedPhone = useUIStore((s) => s.setSelectedInboxPhone)
  const profilePanelCollapsed = useUIStore((s) => s.profilePanelCollapsed)
  const listCollapsed = useUIStore((s) => s.contactsListCollapsed)
  const profileToEditPhone = useUIStore((s) => s.profileToEditPhone)
  const setProfileToEditPhone = useUIStore((s) => s.setProfileToEditPhone)
  // Fil de messages d'un collègue (clic dans la barre latérale).
  const collegueId = useUIStore((s) => s.selectedCollegueId)
  const setCollegue = useUIStore((s) => s.setSelectedCollegue)
  // Manager en essai, mise en route pas finie : l'accueil remplace « Sélectionnez
  // une conversation », sauf s'il l'a masqué. Pendant le chargement, zone vide.
  const aFinir = useMiseEnRouteAFinir()
  const { data: profile } = useProfile()
  const accueilMasque = useUIStore((s) => s.accueilEssaiMasquePar) === profile?.id
  const accueilEssai = aFinir === undefined ? 'attente' : aFinir && !accueilMasque ? 'afficher' : null

  const { makeOutboundCall } = useCallActions()
  const selectedCallerId = useSelectedCallerId()

  // Conversation opened on a search result: the exchange to show.
  const cible = useUIStore((s) => s.inboxEchangeCible)
  const ouvrirSurEchange = useUIStore((s) => s.ouvrirInboxSurEchange)
  const cibleFil = cible && cible.phone === selectedPhone && cible.line === selectedLine ? cible : null

  // Ouvrir un fil le marque lu (textos, vocaux, manqués), quelle que soit la
  // porte d'entrée : clic dans la liste, appel en cours, fiche contact, toast.
  // Une seule requête remplit aussi la fiche, les tâches et l'historique.
  const { pret: filPret } = useOuvrirFil(selectedPhone, selectedLine, cibleFil?.at ?? null)

  const handleSelect = (phone: string, line: string | null, match?: InboxMatch) => {
    setDialerOpen(false)
    if (match?.event_id) ouvrirSurEchange(phone, line, { eventId: match.event_id, at: match.at })
    else setSelectedPhone(phone, line)
  }

  // Un fil qu'on ouvre depuis ailleurs (appel accepté, fiche) ferme le clavier.
  // setDialerOpen est le setter useState du parent : stable.
  useEffect(() => {
    if (selectedPhone || collegueId) setDialerOpen(false)
  }, [selectedPhone, collegueId, setDialerOpen])

  const centerOpen = dialerOpen || !!selectedPhone || !!collegueId

  // Panneau « Détails » d'un appel : prend la place de la fiche, se ferme
  // quand on change de fil.
  const [detailsAppel, setDetailsAppel] = useState<TimelineEvent | null>(null)
  useEffect(() => setDetailsAppel(null), [selectedPhone, selectedLine])

  return (
    <MotionConfig reducedMotion="user">
    <DetailsAppelContext.Provider value={setDetailsAppel}>
      <div
        className={cn(
          'md:flex-none md:border-r flex flex-col',
          listCollapsed ? 'md:w-[60px]' : 'flex-1 md:w-[320px]',
          darkMode ? 'md:border-slate-800' : 'md:border-slate-200',
          !listCollapsed && centerOpen && 'hidden md:flex',
        )}
      >
        <InboxList
          selectedPhone={dialerOpen ? null : selectedPhone}
          selectedLine={selectedLine}
          onSelect={handleSelect}
          onCompose={() => {
            setCollegue(null)
            setDialerOpen(true)
          }}
          composeActive={dialerOpen}
          darkMode={darkMode}
        />
      </div>

      <div className={cn('flex-1 flex min-w-0', !centerOpen && 'hidden md:flex')}>
        {dialerOpen ? (
          <div
            className={cn(
              'relative flex-1 flex flex-col overflow-hidden',
              darkMode ? 'bg-slate-900' : 'bg-slate-50/50',
            )}
          >
            <button
              type="button"
              onClick={() => setDialerOpen(false)}
              title={t('common.close', 'Fermer')}
              className={cn(
                'absolute top-3 right-3 z-10 p-2 rounded-lg transition-colors',
                darkMode
                  ? 'hover:bg-slate-800 text-slate-400 hover:text-white'
                  : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700',
              )}
            >
              <X className="h-4 w-4" />
            </button>
            <DialerPane
              key={pendingDial?.nonce ?? 0}
              darkMode={darkMode}
              initialNumber={pendingDial?.number}
              autoCall={pendingDial?.auto}
              onFerme={() => setDialerOpen(false)}
            />
          </div>
        ) : collegueId ? (
          <FilCollegue key={collegueId} userId={collegueId} darkMode={darkMode} onClose={() => setCollegue(null)} />
        ) : selectedPhone && !filPret ? (
          <div className={cn('flex-1 flex items-center justify-center', darkMode ? 'bg-slate-950' : 'bg-slate-50')}>
            <Loader2 className={cn('h-5 w-5 animate-spin', darkMode ? 'text-slate-600' : 'text-slate-300')} />
          </div>
        ) : selectedPhone ? (
          <ContactDetail
            key={`${selectedLine ?? ''}|${selectedPhone}`}
            phoneNumber={selectedPhone}
            line={selectedLine}
            cible={cibleFil}
            onSelectLine={(line) => setSelectedPhone(selectedPhone, line)}
            darkMode={darkMode}
            onClose={() => setSelectedPhone(null)}
          />
        ) : (
          <div
            className={cn(
              'flex-1 flex items-center justify-center',
              darkMode ? 'bg-slate-950' : 'bg-slate-50',
            )}
          >
            {accueilEssai === 'attente' ? null : accueilEssai === 'afficher' ? (
              <AccueilEssai darkMode={darkMode} onComposer={() => setDialerOpen(true)} />
            ) : (
              <div className="text-center max-w-xs">
                <MessageSquare
                  className={cn(
                    'h-12 w-12 mx-auto mb-3 opacity-30',
                    darkMode ? 'text-slate-600' : 'text-slate-300',
                  )}
                />
                <p className={cn('text-sm', darkMode ? 'text-slate-500' : 'text-slate-400')}>
                  {t('sms.selectConvTitle', 'Sélectionnez une conversation')}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fiche (3e colonne, desktop seulement) */}
      {/* 3e colonne (desktop) : la fiche, ou le panneau « Détails » d'un appel
          qui glisse depuis la droite et repart par la droite. */}
      <AnimatePresence mode="wait" initial={false}>
        {selectedPhone && !dialerOpen && detailsAppel ? (
          <motion.div
            key="details"
            initial={{ x: 32, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 32, opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className={cn(
              'hidden md:flex w-[380px] flex-shrink-0 border-l',
              darkMode ? 'border-slate-800' : 'border-slate-200',
            )}
          >
            <DetailsAppel
              key={detailsAppel.id}
              event={detailsAppel}
              phoneNumber={selectedPhone}
              darkMode={darkMode}
              onClose={() => setDetailsAppel(null)}
            />
          </motion.div>
        ) : selectedPhone && !dialerOpen && !profilePanelCollapsed && filPret ? (
          <motion.div
            key="fiche"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className={cn(
              'hidden md:flex w-[320px] flex-shrink-0 border-l',
              darkMode ? 'border-slate-800' : 'border-slate-200',
            )}
          >
            <ContactProfile
              phoneNumber={selectedPhone}
              darkMode={darkMode}
              onCall={(num) => void makeOutboundCall(num, selectedCallerId || undefined)}
              editerPremierVide={profileToEditPhone === selectedPhone}
              onEditionLancee={() => setProfileToEditPhone(null)}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </DetailsAppelContext.Provider>
    </MotionConfig>
  )
}
